import React from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { styled } from '@mui/material';
import { TextLinearProgress } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/styled/BarComponents';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { ContentMetricType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import colors from 'src/utils/colors';
import { Box } from '@mui/material';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { useLastWeek } from 'src/hooks/useLastWeek';
import {
  KeyMetricsFilterAdapter
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/KeyMetricsAdapter';
import {
  UtilizationData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import { formatContentUtilizationData, FormattedContentData, ContentUtilizationTitles } from './utils';
import { RightArrow } from 'src/components/SvgIcons/SvgIcons';
import { useScrollToId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useScrollToId';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { useContentUtilizationStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/ContentUtilizationStore';
import { ContentUtilizationFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/ContentUtilizationStore';
import TextArrow from '../TextArrow';
import { getItemHighlightsDisplayConfig } from 'src/utils/app';
import { ContentFieldLabel } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/NewFieldTag';

export interface ContentBarPercentData {
  keyMetricsAdapter: KeyMetricsFilterAdapter;
  productUtilizationData?: UtilizationData[];
}

const LegendPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pillColor'
})<{ pillColor: string }>(({ pillColor }) => ({
  width: 12,
  height: 3,
  backgroundColor: pillColor,
  borderRadius: 3,
  display: 'inline-block'
}));

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <Flex alignItems="center" gap={8}>
    <LegendPill pillColor={color} />
    <Text variant="body2">{label}</Text>
  </Flex>
);

interface PercentBarsProps {
  primaryData: [string, number][];
  secondaryData: [string, number][];
  formattedData: FormattedContentData;
}

const PercentBars = ({ primaryData, secondaryData, formattedData }: PercentBarsProps) => {
  const scrollToId = useScrollToId<typeof ContentSectionId>();
  const { toggleFilter, setAllFilters } = useContentUtilizationStore();
  const titleToFilterKey: Partial<Record<ContentUtilizationTitles, keyof ContentUtilizationFilters>> = {
    [ContentUtilizationTitles.Title]: 'titleUtilization',
    [ContentUtilizationTitles.ItemHighlights]: 'itemHighlightsUtilization',
    [ContentUtilizationTitles.Bullets]: 'bulletUtilization',
    [ContentUtilizationTitles.Descriptions]: 'descriptionUtilization',
    [ContentUtilizationTitles.Images]: 'imageUtilization',
    [ContentUtilizationTitles.Videos]: 'videoUtilization',
    [ContentUtilizationTitles.Enhanced]: 'enhancedUtilization'
  };

  const handleViewLowUtilizationClick = async (
    name: ContentUtilizationTitles,
    type?: ContentSectionId,
    titleLink?: 'lowUtilization' | 'overLimit'
  ) => {
    if (type) {
      await scrollToId(type);
    } else {
      await scrollToId(ContentSectionId.TableGrid);
      if (name === ContentUtilizationTitles.TotalContentUtilization) {
        setAllFilters(true);
      } else if (name === ContentUtilizationTitles.Title && titleLink === 'overLimit') {
        toggleFilter('titleOverLimit', true);
      } else {
        const filterKey = titleToFilterKey[name];
        if (filterKey) {
          toggleFilter(filterKey, true);
        }
      }
    }
  };

  const { isProductEntity } = useProductEntity();

  return (
    <Flex flexDirection="column" gap={24}>
      {primaryData.map((datum, index) => {
        const item = formattedData[index];
        return (
          <Flex key={index} gap={28} flexDirection="column">
            <Flex gap={28}>
              <TextLinearProgress
                mainValue={{ value: datum[1], color: colors.darkBlue }}
                comparisonValue={{ value: secondaryData[index][1], color: colors.magenta }}
              />
              <Flex
                width="441px"
                flexDirection="column"
                sx={{
                  flexShrink: 0,
                  '& .MuiTypography-root': {
                    lineHeight: '24px'
                  }
                }}
              >
                <Text
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  <ContentFieldLabel title={datum[0]} />
                </Text>
                <Text
                  variant="body2"
                  sx={{
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: isProductEntity ? 'pointer' : 'default',
                    '&:hover': {
                      textDecoration: isProductEntity ? 'underline' : 'none',
                      '& .hover-arrow': {
                        transform: 'translateX(5px)'
                      }
                    }
                  }}
                  onClick={() => {
                    if (isProductEntity) {
                      handleViewLowUtilizationClick(item.title, item.scrollToId);
                    }
                  }}
                >
                  {item.details}
                  {isProductEntity && (
                    <RightArrow
                      style={{
                        marginLeft: '4px',
                        height: '16px',
                        width: '16px',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      className="hover-arrow"
                    />
                  )}
                </Text>
                {!isProductEntity && (
                  <TextArrow
                    onClick={() =>
                      handleViewLowUtilizationClick(
                        item.title,
                        undefined,
                        item.title === ContentUtilizationTitles.Title ? item.preferredTitleLink : undefined
                      )
                    }
                  >
                    {item.title === ContentUtilizationTitles.Title && item.preferredTitleLink === 'overLimit' ? (
                      <>
                        View{' '}
                        <span style={{ fontWeight: 500, margin: '0 4px' }}>
                          {(item.overLimitProducts || 0).toLocaleString()} products
                        </span>{' '}
                        over 75 characters
                      </>
                    ) : (
                      <>
                        View{' '}
                        <span style={{ fontWeight: 500, margin: '0 4px' }}>
                          {item.lowProducts.toLocaleString()} products
                        </span>{' '}
                        with low utilization
                      </>
                    )}
                  </TextArrow>
                )}
              </Flex>
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};

const UtilizationContentBarPercent = ({ forceLoading = false }: { forceLoading?: boolean }) => {
  const { getContentScoresKeyMetrics } = useContentService();
  const { isProductEntity } = useProductEntity();
  const { getProductLevelContentUtilizationDetails } = useContentService();
  const comparisonTimeLabel = useAppSelector((state) => state.comparisonTimePeriod.shortDisplayName);
  const entityName = useAppSelector((state) => state.entityService.mainEntity?.name);
  const { mainTimePeriod, comparisonTimePeriod, isLoading: isLastWeekLoading } = useLastWeek();
  const { showItemHighlights } = getItemHighlightsDisplayConfig();

  // Wait for useLastWeek to finish loading before running the query
  const isTimePeriodReady = !isLastWeekLoading && !!comparisonTimePeriod;

  return (
    <Flex flexDirection="column" gap={8}>
      <BeaconProChart
        name={`content-bar-percent`}
        enabled={isTimePeriodReady}
        forceLoading={forceLoading || !isTimePeriodReady}
        queryFn={async () => {
          const data = await getContentScoresKeyMetrics(
            ContentMetricType.ContentUtilizationScore,
            {
              startWeek: mainTimePeriod.startWeek,
              endWeek: mainTimePeriod.endWeek
            },
            {
              startWeek: comparisonTimePeriod!.startWeek,
              endWeek: comparisonTimePeriod!.endWeek
            },
            {
              filterTitleInaccuracies: false,
              filterBulletPointsInaccuracies: false,
              filterDescriptionInaccuracies: false,
              filterImageInaccuracies: false,
              filterVideoInaccuracies: false,
              minimumRequiredContentUtilizationScore: 0.7,
              minimumRequiredTitleUtilizationScore: 0.7,
              minimumRequiredBulletPointsUtilizationScore: 0.7,
              minimumRequiredImageUtilizationScore: 0.7,
              minimumRequiredDescriptionUtilizationScore: 0.7,
              minimumRequiredVideoUtilizationScore: 0.99, // it means that the video is required to be 100% utilized
              minimumRequiredAPlusUtilizationScore: 0,
              ...(showItemHighlights ? { minimumRequiredItemHighlightsUtilizationScore: 0.7 } : {})
            }
          );
          let productUtilizationData: UtilizationData[] | undefined;
          if (isProductEntity) {
            const res = await getProductLevelContentUtilizationDetails();
            productUtilizationData = res.transform();
          }

          const keyMetricsAdapter = new KeyMetricsFilterAdapter(data, {
            aPlusScore: ContentUtilizationTitles.Enhanced,
            descriptionScore: ContentUtilizationTitles.Descriptions,
            featureBulletsScore: ContentUtilizationTitles.Bullets,
            imagesScore: ContentUtilizationTitles.Images,
            titleScore: ContentUtilizationTitles.Title,
            ...(showItemHighlights ? { itemHighlightsScore: ContentUtilizationTitles.ItemHighlights } : {}),
            videosScore: ContentUtilizationTitles.Videos,
            contentUtilizationScore: ContentUtilizationTitles.TotalContentUtilization
          });

          return {
            keyMetricsAdapter,
            productUtilizationData
          };
        }}
      >
        <BeaconProChart.Header>
          <BeaconProChart.Title>
            <Text variant="h4">Content Utilization</Text>
          </BeaconProChart.Title>
        </BeaconProChart.Header>
        <BeaconProChart.Subtitle
          render={(data: ContentBarPercentData) => {
            // Handle null data case (loading state)
            if (!data?.keyMetricsAdapter) {
              return null;
            }
            const { rawData } = data.keyMetricsAdapter.transform();
            const mainKeyMetrics = rawData?.mainKeyMetrics;
            const contentUtilizationScore = Math.round(mainKeyMetrics.contentUtilizationScore * 100);
            const elementCountLabel = showItemHighlights ? 'seven' : 'six';
            return (
              <Text variant="body1">
                {isProductEntity ? 'This product' : entityName} earned a {contentUtilizationScore}% total content
                utilization score, which is an average of all {elementCountLabel} element scores. The data represents
                the last week period compared to the {comparisonTimeLabel.toLowerCase()}.
              </Text>
            );
          }}
        />
        <BeaconProChart.Visualization
          render={(data: ContentBarPercentData) => {
            // Handle null data case (loading state)
            if (!data?.keyMetricsAdapter) {
              return null;
            }
            const { rawData } = data.keyMetricsAdapter.transform();
            const productUtilizationData: UtilizationData[] = data.productUtilizationData || [];
            const formattedData = formatContentUtilizationData(
              rawData,
              isProductEntity,
              productUtilizationData,
              showItemHighlights
            );
            const primaryData = formattedData.map((item) => [item.title, item.primary]) as [string, number][];
            const secondaryData = formattedData.map((item) => [item.title, item.secondary]) as [string, number][];
            return (
              <>
                <Flex
                  gap={32}
                  sx={{
                    margin: '32px 0px'
                  }}
                >
                  <LegendItem color={colors.magenta} label={comparisonTimeLabel} />
                  <LegendItem color={colors.darkBlue} label="Last Week" />
                </Flex>
                <PercentBars primaryData={primaryData} secondaryData={secondaryData} formattedData={formattedData} />
              </>
            );
          }}
        />
      </BeaconProChart>
    </Flex>
  );
};

export default UtilizationContentBarPercent;
